import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components/macro";
import { Button, Switch } from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import CloudUpload from "@material-ui/icons/CloudUpload";
import InsertDriveFile from "@material-ui/icons/InsertDriveFile";
import { PrinterList } from "../PrinterDb/printer_list";
import { UserSearchField } from "../PrinterDb/cancel_reason_modal";
import { CircularProgressWithLabel } from "../CircularProgressWithLabel";
import {
  singleFileUploadHandler,
  getPdfPageCount,
  convertAndCountPages,
  isSupportedFileType,
} from "../PrinterDb/printerHelper";
import { getPost } from "../utils/commHelper";
import { useToast } from "../PrinterDb/toast";
import { theme } from "../PrinterDb/theme";

const UploadFileFullScreen = ({ printers, isDev = false }) => {
  const [file, setFile] = useState(null);
  const [printer, setPrinter] = useState(null);
  const [color, setColor] = useState(false);
  const [duplex, setDuplex] = useState(false);
  const [copies, setCopies] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [free, setFree] = useState(false);
  const [pages, setPages] = useState(null);
  const [pagesDetecting, setPagesDetecting] = useState(false);
  // Distinct from pagesDetecting: this stage hits the paid /convertToPdf API
  // (only for file types the free local detection can't count — see printerHelper.js)
  // and takes longer, so it gets its own "Converting…" message rather than blending
  // into "Detecting…".
  const [converting, setConverting] = useState(false);
  const [unsupportedFile, setUnsupportedFile] = useState(false);
  // Set only when convertAndCountPages ran — the RPI must print the converted
  // PDF, not the original file, so these override what gets sent at submit time.
  const [printableLink, setPrintableLink] = useState(null);
  const [printableTitle, setPrintableTitle] = useState(null);
  const [userResults, setUserResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [progressPercents, setProgressPercents] = useState(null);
  const inputRef = useRef();
  const searchTimer = useRef(null);
  const showToast = useToast();

  useEffect(() => {
    let cancelled = false;

    const resetDetectionState = () => {
      setPages(null);
      setPagesDetecting(false);
      setConverting(false);
      setUnsupportedFile(false);
      setPrintableLink(null);
      setPrintableTitle(null);
    };

    if (!file) {
      resetDetectionState();
      return undefined;
    }

    if (!isSupportedFileType(file)) {
      resetDetectionState();
      setUnsupportedFile(true);
      return undefined;
    }

    resetDetectionState();
    setPagesDetecting(true);

    (async () => {
      const localCount = await getPdfPageCount(file);
      if (cancelled) return;
      setPagesDetecting(false);

      if (localCount) {
        setPages(localCount);
        return;
      }

      // Free local detection couldn't count this file (e.g. XLSX, TXT, legacy
      // .doc/.xls/.ppt, or a DOCX missing its cached page count) — fall back to
      // the paid conversion path.
      setConverting(true);
      const result = await convertAndCountPages(file, isDev);
      if (cancelled) return;
      setConverting(false);

      if (result) {
        setPages(result.pages);
        setPrintableLink(result.pdfUrl);
        setPrintableTitle(result.pdfFileName);
      } else {
        setPages(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [file, isDev]);

  const onSearchUser = useCallback(
    (value) => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
      if (!value || value.trim().length < 2) {
        setUserResults([]);
        return;
      }
      searchTimer.current = setTimeout(async () => {
        setSearching(true);
        try {
          const res = await getPost("/searchUserByDynamic", [value.trim()], isDev);
          const ok = res?.data?.result?.ok ?? res?.data?.ok ?? [];
          setUserResults(Array.isArray(ok) ? ok : []);
        } catch (e) {
          console.error("[Printer] user search failed", e);
          setUserResults([]);
        } finally {
          setSearching(false);
        }
      }, 350);
    },
    [isDev]
  );

  const resetForm = () => {
    if (inputRef.current) inputRef.current.value = "";
    setFile(null);
    setPrinter(null);
    setColor(false);
    setDuplex(false);
    setCopies(1);
    setSelectedUser(null);
    setFree(false);
    setSubmitting(false);
    setProgressPercents(null);
    setPrintableLink(null);
    setPrintableTitle(null);
  };

  const hasValidPages = Number(pages) >= 1;
  const canSubmit =
    !!file &&
    !!printer &&
    !!selectedUser?.user &&
    !submitting &&
    !pagesDetecting &&
    !converting &&
    hasValidPages;

  const submitPrintJob = async ({ url, title }) => {
    setSubmitting(true);
    try {
      await getPost(
        "/dashboardCreatePrint",
        {
          userId: selectedUser.user,
          free: !!free,
          printerId: printer.printer_id,
          roomId: printer.room_id,
          link: url,
          title,
          pages: Number(pages || 1),
          settings: {
            color: color ? "color" : "bw",
            duplex: !!duplex,
            copies: Number(copies || 1),
          },
        },
        isDev
      );
      showToast(`Print job "${title}" sent to ${printer.room_name || "printer"}.`, "success");
      resetForm();
    } catch (e) {
      console.error("[Printer] create print failed", e);
      showToast(e?.response?.data?.error || e?.message || "Failed to create print job", "error");
      setSubmitting(false);
    }
  };

  const onStartPrintPress = () => {
    if (!canSubmit) return;

    if (printableLink) {
      // convertAndCountPages already produced a hosted PDF — the RPI must print
      // that, not the original (unprintable-as-is) file, so skip the upload step.
      submitPrintJob({ url: printableLink, title: printableTitle || file.name });
      return;
    }

    setProgressPercents(0);
    singleFileUploadHandler(
      file,
      "operation_upload_printer_files",
      (url) => submitPrintJob({ url, title: file.name }),
      (progress) => setProgressPercents(progress)
    );
  };

  return (
    <Wrapper>
      <Header>
        <Title>Print file</Title>
        <Subtitle>Upload a file, choose a printer, and set the print properties.</Subtitle>
      </Header>

      <Columns>
        <LeftColumn>
          {!file ? (
            <DropZone>
              <CloudUpload style={{ fontSize: 32, color: theme.primary }} />
              <DropZoneText>Click to choose a file to print</DropZoneText>
              <UploadInput
                ref={inputRef}
                type="file"
                onChange={(e) => setFile(e.target.files[0])}
              />
            </DropZone>
          ) : (
            <>
              <FileRow>
                <InsertDriveFile style={{ fontSize: 18, color: theme.primaryHover }} />
                <FileName>{file.name}</FileName>
                <ChangeFileBtn
                  type="button"
                  onClick={() => {
                    if (inputRef.current) inputRef.current.value = "";
                    setFile(null);
                    setPrinter(null);
                  }}
                >
                  Change
                </ChangeFileBtn>
              </FileRow>
              <PrinterPickerLabel>Choose printer</PrinterPickerLabel>
              <PrinterPickerWrapper>
                <PrinterList printers={printers} onChoosePrint={setPrinter} />
              </PrinterPickerWrapper>
            </>
          )}
        </LeftColumn>

        <RightColumn>
          <SettingsTitle>Properties</SettingsTitle>

          <Section>
            <UserSearchField
              selectedUser={selectedUser}
              onSelect={setSelectedUser}
              onSearch={onSearchUser}
              results={userResults}
              searching={searching}
            />
          </Section>

          <SettingsCard>
            <Row>
              <RowLabel>Billing</RowLabel>
              <BillingToggle>
                <BillingOption checked={!free}>Paid</BillingOption>
                <IOSSwitch checked={!!free} onChange={(e) => setFree(e.target.checked)} />
                <BillingOption checked={!!free}>Free</BillingOption>
              </BillingToggle>
            </Row>
            <Divider />
            <Row>
              <RowLabel>Color</RowLabel>
              <IOSSwitch checked={color} onChange={(e) => setColor(e.target.checked)} />
            </Row>
            <Divider />
            <Row>
              <RowLabel>Duplex</RowLabel>
              <IOSSwitch checked={duplex} onChange={(e) => setDuplex(e.target.checked)} />
            </Row>
            <Divider />
            <Row>
              <RowLabel>Pages</RowLabel>
              <PagesValue detected={hasValidPages}>
                {!file
                  ? "—"
                  : unsupportedFile
                  ? "Unsupported file"
                  : pagesDetecting
                  ? "Detecting…"
                  : converting
                  ? "Converting…"
                  : hasValidPages
                  ? `${pages} page${pages === 1 ? "" : "s"}`
                  : "Not detected"}
              </PagesValue>
            </Row>
            <Divider />
            <Row>
              <RowLabel>Copies</RowLabel>
              <CopiesSelect onChange={(e) => setCopies(e.target.value)} value={copies}>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </CopiesSelect>
            </Row>
          </SettingsCard>

          {file && unsupportedFile ? (
            <Hint>
              This file type isn't supported for printing. Try a PDF, Word, PowerPoint, Excel,
              text, or image file.
            </Hint>
          ) : file && !pagesDetecting && !converting && !hasValidPages ? (
            <Hint>
              Couldn't auto-detect the page count for this file (including via conversion), so
              it can't be printed from here — manual entry is disabled to prevent
              under/over-billing. Try a different file.
            </Hint>
          ) : null}

          {printer ? (
            <SummaryCard>
              <SummaryRow>
                <SummaryLabel>Printer</SummaryLabel>
                <SummaryValue>{printer.room_name}</SummaryValue>
              </SummaryRow>
            </SummaryCard>
          ) : null}

          <ButtonWrapper>
            <SubmitButton fullWidth variant="contained" disabled={!canSubmit} onClick={onStartPrintPress}>
              {submitting
                ? "Sending…"
                : converting
                ? "Converting file…"
                : pagesDetecting
                ? "Detecting pages…"
                : "Print file"}
            </SubmitButton>
          </ButtonWrapper>
        </RightColumn>
      </Columns>

      {progressPercents !== null ? (
        <ProgressOverlay>
          <ProgressBox>
            <CircularProgressWithLabel value={progressPercents} />
            <ProgressLabel>Uploading file…</ProgressLabel>
          </ProgressBox>
        </ProgressOverlay>
      ) : null}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
  padding: 24px;
  font-family: ${theme.fontFamily};
`;

const Header = styled.div`
  margin-bottom: 20px;
`;

const Title = styled.div`
  font-size: 19px;
  font-weight: 600;
  color: ${theme.text};
`;

const Subtitle = styled.div`
  font-size: 13px;
  color: ${theme.textFaint};
  margin-top: 2px;
`;

const Columns = styled.div`
  display: flex;
  gap: 24px;
  align-items: flex-start;
`;

const LeftColumn = styled.div`
  flex: 1;
  min-width: 0;
  background: ${theme.surface};
  border-radius: ${theme.radius};
  border: 1px solid ${theme.border};
  overflow: hidden;
`;

const RightColumn = styled.div`
  flex: 0 0 420px;
  background: ${theme.surface};
  border-radius: ${theme.radius};
  border: 1px solid ${theme.border};
  padding: 20px;
`;

const DropZone = styled.label`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  height: 300px;
  margin: 20px;
  border: 2px dashed ${theme.borderStrong};
  border-radius: ${theme.radiusSm};
  cursor: pointer;
  background: ${theme.background};
  transition: border-color 150ms ease;

  &:hover {
    border-color: ${theme.primary};
  }
`;

const DropZoneText = styled.div`
  font-size: 14px;
  color: ${theme.textMuted};
`;

const UploadInput = styled.input`
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
`;

const FileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px 20px;
  border-bottom: 1px solid ${theme.border};
`;

const FileName = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 13px;
  font-weight: 500;
  color: ${theme.text};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ChangeFileBtn = styled.button`
  flex-shrink: 0;
  border: none;
  background: transparent;
  color: ${theme.primaryHover};
  font-family: ${theme.fontFamily};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
`;

const PrinterPickerLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${theme.textMuted};
  padding: 16px 20px 0;
`;

const PrinterPickerWrapper = styled.div`
  max-height: 460px;
  overflow-y: auto;
`;

const SettingsTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${theme.text};
  margin-bottom: 12px;
`;

const Section = styled.div`
  margin-bottom: 12px;
`;

const SettingsCard = styled.div`
  border: 1px solid ${theme.border};
  border-radius: ${theme.radiusSm};
  overflow: hidden;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
`;

const RowLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${theme.text};
`;

const Divider = styled.div`
  height: 1px;
  background: ${theme.border};
`;

const BillingToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const BillingOption = styled.span`
  font-size: 13px;
  font-weight: ${({ checked }) => (checked ? 600 : 500)};
  color: ${({ checked }) => (checked ? theme.text : theme.textFaint)};
`;

const PagesValue = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ detected }) => (detected ? theme.text : theme.textFaint)};
`;

const CopiesSelect = styled.select`
  width: 110px;
  padding: 8px 10px;
  font-family: ${theme.fontFamily};
  font-size: 14px;
  color: ${theme.text};
  background: ${theme.surface};
  border: 1px solid ${theme.border};
  border-radius: ${theme.radiusSm};
  outline: none;
  cursor: pointer;

  &:focus {
    border-color: ${theme.primary};
    box-shadow: 0 0 0 3px ${theme.primaryLight};
  }
`;

const Hint = styled.div`
  margin-top: 12px;
  font-size: 12px;
  color: ${theme.danger};
`;

const SummaryCard = styled.div`
  margin-top: 16px;
  padding: 12px 16px;
  background: ${theme.background};
  border-radius: ${theme.radiusSm};
`;

const SummaryRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: 13px;
`;

const SummaryLabel = styled.span`
  color: ${theme.textFaint};
`;

const SummaryValue = styled.span`
  color: ${theme.textMuted};
`;

const ButtonWrapper = styled.div`
  margin-top: 20px;
`;

const ProgressOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.85);
  border-radius: ${theme.radius};
`;

const ProgressBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: ${theme.textMuted};
  font-family: ${theme.fontFamily};
`;

const ProgressLabel = styled.div`
  font-size: 13px;
`;

export const IOSSwitch = withStyles((muiTheme) => ({
  root: {
    width: 42,
    height: 26,
    padding: 0,
    margin: muiTheme.spacing(1),
  },
  switchBase: {
    padding: 1,
    "&$checked": {
      transform: "translateX(16px)",
      color: muiTheme.palette.common.white,
      "& + $track": {
        backgroundColor: theme.primary,
        opacity: 1,
        border: "none",
      },
    },
    "&$focusVisible $thumb": {
      color: theme.primary,
      border: "6px solid #fff",
    },
  },
  thumb: {
    width: 24,
    height: 24,
  },
  track: {
    borderRadius: 26 / 2,
    border: `1px solid ${muiTheme.palette.grey[400]}`,
    backgroundColor: muiTheme.palette.grey[50],
    opacity: 1,
    transition: muiTheme.transitions.create(["background-color", "border"]),
  },
  checked: {},
  focusVisible: {},
}))(({ classes, ...props }) => (
  <Switch
    focusVisibleClassName={classes.focusVisible}
    disableRipple
    classes={{
      root: classes.root,
      switchBase: classes.switchBase,
      thumb: classes.thumb,
      track: classes.track,
      checked: classes.checked,
    }}
    {...props}
  />
));

const SubmitButton = withStyles(() => ({
  root: {
    color: "white",
    backgroundColor: theme.primary,
    borderRadius: theme.radiusSm,
    textTransform: "none",
    fontFamily: theme.fontFamily,
    fontWeight: 600,
    boxShadow: "none",
    padding: "10px 24px",
    "&:hover": {
      backgroundColor: theme.primaryHover,
      boxShadow: theme.shadow,
    },
    "&:disabled": {
      backgroundColor: theme.border,
      color: theme.textFaint,
    },
  },
}))(Button);

export { UploadFileFullScreen };
